import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { resolveSession } from "@/lib/study.functions";
import { ensureTelegram, getDeviceId, getTelegram, initTelegram } from "@/lib/telegram";
import { useAuth } from "@/hooks/use-auth";
import type { Profile } from "@/lib/app-types";

type SessionState = {
  status: "loading" | "signed_out" | "needs_name" | "ready" | "error";
  profile: Profile | null;
  error: string | null;
  registerName: (fullName: string) => Promise<void>;
  applyProfile: (profile: Profile) => void;
  retry: () => void;
};

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<SessionState["status"]>("loading");
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    if (authLoading) {
      setStatus("loading");
      return;
    }

    (async () => {
      try {
        // Only Telegram webviews pay for the Telegram SDK; elsewhere this is a
        // no-op that resolves immediately.
        await ensureTelegram();
        if (cancelled) return;
        initTelegram();
        const initData = getTelegram()?.initData || undefined;

        // Telegram Mini App: identity comes from the signed initData payload,
        // no Google popup (blocked inside Telegram's webview).
        if (initData) {
          const tgResult = await resolveSession({ data: { initData } });
          if (cancelled) return;
          if (tgResult.status === "ok" && tgResult.profile) {
            setProfile(tgResult.profile);
            setStatus("ready");
            return;
          }
        }

        if (!user) {
          setProfile(null);
          setStatus("signed_out");
          return;
        }

        const result = await resolveSession({ data: { initData } });
        if (cancelled) return;
        if (result.status === "ok" && result.profile) {
          setProfile(result.profile);
          setStatus("ready");
          return;
        }
        setError("Could not start your session");
        setStatus("error");
      } catch (e) {
        if (cancelled) return;
        setError(
          typeof navigator !== "undefined" && navigator.onLine === false
            ? "You're offline — check your connection and try again."
            : e instanceof Error
              ? e.message
              : "Could not start your session",
        );
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [nonce, user, authLoading]);

  const registerName = useCallback(async (fullName: string) => {
    const result = await resolveSession({
      data: { fullName, deviceId: getDeviceId() },
    });
    if (result.status === "ok" && result.profile) {
      setProfile(result.profile);
      setStatus("ready");
      return;
    }
    throw new Error("Could not create your profile");
  }, []);

  const applyProfile = useCallback((next: Profile) => {
    setProfile(next);
  }, []);

  const retry = useCallback(() => {
    setStatus("loading");
    setError(null);
    setNonce((n) => n + 1);
  }, []);

  return (
    <SessionContext.Provider value={{ status, profile, error, registerName, applyProfile, retry }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside SessionProvider");
  return ctx;
}